import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaFolderOpen, FaSpinner, FaArrowLeft, FaPlus, FaUpload, FaFileAlt } from 'react-icons/fa';
import ClientLayout from '../layouts/ClientLayout';
import SubbieLayout from '../layouts/SubbieLayout';
import { API_URL } from '../config';
import ProjectGantt from '../components/ProjectGantt';
import PortalProjectMembers from '../components/PortalProjectMembers';

// ============================================
// PROJECT PORTAL — the client / subbie view of a project.
// Read-only unless they have edit rights on the project
// (Viewer = read-only · Editor = add + edit/delete their own).
// Deliberately shows NO cost, margin, payables or other members.
// ============================================

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : 'TBC');

export default function PortalProjects({ role = 'client', onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const Layout = role === 'subbie' ? SubbieLayout : ClientLayout;
  const base = role === 'subbie' ? '/subbie-projects' : '/client-projects';
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [list, setList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', start_date: '', end_date: '', location: '', description: '' });
  // Add task / add phase prompt (was window.prompt — the OS black box).
  const [addWhat, setAddWhat] = useState(null); // 'task' | 'phase' | null
  const [addName, setAddName] = useState('');
  // Richer task fields (matching the admin modal): phase, dates, allocation.
  const [addPhaseId, setAddPhaseId] = useState('');
  const [addStart, setAddStart] = useState('');
  const [addEnd, setAddEnd] = useState('');
  const [addAssignee, setAddAssignee] = useState('');

  const authHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/projects`, { headers: authHeaders() });
      const d = await r.json();
      setList(d.projects || []);
    } catch (e) { toast.error('Could not load projects'); }
    finally { setLoading(false); }
  }, []);

  const loadOne = useCallback(async (projectId) => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/projects/${projectId}`, { headers: authHeaders() });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Project not found');
      setData(d);
    } catch (e) {
      toast.error(e.message || 'Could not load the project');
      navigate(base);
    } finally { setLoading(false); }
  }, [base, navigate]);

  useEffect(() => { if (id) loadOne(id); else loadList(); }, [id, loadList, loadOne]);

  const post = async (path, body, method = 'POST') => {
    const r = await fetch(`${API_URL}${path}`, { method, headers: authHeaders(), body: JSON.stringify(body || {}) });
    const d = await r.json();
    if (!d.success) throw new Error(d.error || 'Request failed');
    return d;
  };

  const canEdit = !!data?.permissions?.can_edit;
  // The owning client (and anyone with edit rights) may manage who sees the project.
  const canManage = !!(data?.permissions?.can_manage || data?.permissions?.can_edit);
  const project = data?.project || {};
  const phases = data?.phases || [];
  const tasks = data?.tasks || [];
  const files = data?.files || [];
  const done = tasks.filter((t) => t.status === 'done').length;
  const progress = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  const addTask = () => openAdd('task');
  const addPhase = () => openAdd('phase');

  const openAdd = (what) => {
    setAddWhat(what);
    setAddName('');
    setAddPhaseId('');
    setAddStart('');
    setAddEnd('');
    setAddAssignee('');
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    const name = addName.trim();
    if (!name) { toast.error('Give it a name'); return; }
    const isPhase = addWhat === 'phase';
    try {
      setBusy(true);
      if (isPhase) {
        // Phases fall back to the project's dates on the server.
        await post(`/projects/${id}/phases`, {
          name,
          sort_order: phases.length,
          start_date: addStart || undefined,
          end_date: addEnd || undefined,
        });
      } else {
        const d = await post(`/projects/${id}/tasks`, {
          name,
          phase_id: addPhaseId || null,
          start_date: addStart || null,
          end_date: addEnd || null,
        });
        // Allocation is a separate call, same as the admin project page does.
        if (addAssignee && d && d.task && d.task.id) {
          const parts = String(addAssignee).split('|');
          await post(`/projects/tasks/${d.task.id}/assignees`, {
            user_id: parts[0] || null,
            name: parts[1] || '',
            email: parts[2] || '',
            is_external: false,
          });
        }
      }
      setAddWhat(null); setAddName(''); setAddPhaseId(''); setAddStart(''); setAddEnd(''); setAddAssignee('');
      loadOne(id);
      toast.success(isPhase ? 'Phase added' : 'Task added');
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  // Client-owned projects: the backend pins client_id to the caller's own company.
  const canCreate = role === 'client';
  const createProject = async (e) => {
    e.preventDefault();
    if (!newForm.name.trim()) { toast.error('Give the project a name'); return; }
    try {
      setBusy(true);
      const d = await post('/projects', newForm);
      setShowNew(false);
      setNewForm({ name: '', start_date: '', end_date: '', location: '', description: '' });
      toast.success('Project created');
      if (d.project?.id) navigate(`${base}/${d.project.id}`); else loadList();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    try {
      setBusy(true);
      const fd = new FormData();
      fd.append('image', file);
      const ur = await fetch(`${API_URL}/uploads`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: fd });
      const ud = await ur.json();
      if (!ud.success) throw new Error(ud.error || 'Upload failed');
      await post(`/projects/${id}/files`, { file_url: ud.url || ud.file_url, file_name: file.name, file_type: 'other', caption: file.name });
      loadOne(id);
      toast.success('File uploaded');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };


  // ── LIST ──
  if (!id) {
    return (
      <Layout onLogout={onLogout} user={user} activeTab="projects">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
            <div>
              <h1 className="text-2xl font-bold text-navy flex items-center gap-2 mb-1">
                <FaFolderOpen className="text-green" /> Projects
              </h1>
              <p className="text-sm text-gray-500">
                {canCreate ? 'Your projects — create one, or those One Purpose shares with you.' : 'Projects you have been given access to.'}
              </p>
            </div>
            {canCreate && (
              <button onClick={() => setShowNew(true)} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy flex items-center gap-2 shrink-0">
                <FaPlus size={11} /> New project
              </button>
            )}
          </div>
          {loading ? (
            <div className="py-16 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={24} /></div>
          ) : list.length === 0 ? (
            <div className="bg-white rounded-2xl border p-8 text-center text-gray-500" style={{ borderColor: '#E8ECEF' }}>
              {canCreate ? 'No projects yet — use “New project” to set one up.' : 'No projects yet — they appear here as soon as One Purpose gives you access.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {list.map((p) => (
                <Link key={p.id} to={`${base}/${p.id}`} className="bg-white rounded-2xl border p-5 hover:shadow-md transition-shadow" style={{ borderColor: '#E8ECEF' }}>
                  <p className="text-xs font-semibold text-gray-400">{p.project_number}</p>
                  <p className="text-lg font-bold text-navy">{p.name}</p>
                  <p className="text-xs text-gray-500 mb-2">{fmtDate(p.start_date)} → {fmtDate(p.end_date)}</p>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-green" style={{ width: `${p.task_count ? Math.round(((p.task_done || 0) / p.task_count) * 100) : 0}%` }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{p.task_done || 0}/{p.task_count || 0} tasks done</p>
                </Link>
              ))}
            </div>
          )}
        {addWhat && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAddWhat(null)}></div>
            <form onSubmit={submitAdd} className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
              <h2 className="text-xl font-bold text-navy mb-1">{addWhat === 'phase' ? 'New phase' : 'New task'}</h2>
              <p className="text-xs text-gray-500 mb-4">
                {addWhat === 'phase'
                  ? 'Phases group your tasks — e.g. Transport, Documentation, Delivery. It appears straight away on the project schedule.'
                  : 'Give it a clear name — e.g. Book abnormal load permit. You can set dates, requirements and people on the project page.'}
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">{addWhat === 'phase' ? 'Phase name *' : 'Task name *'}</label>
              <input autoFocus value={addName} onChange={(e) => setAddName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder={addWhat === 'phase' ? 'e.g. Transport' : 'e.g. Book abnormal load permit'} />
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setAddWhat(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={busy} className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-green hover:bg-green-600 disabled:opacity-50">{addWhat === 'phase' ? 'Add phase' : 'Add task'}</button>
              </div>
            </form>
          </div>
        )}

        {showNew && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowNew(false)}></div>
            <form onSubmit={createProject} className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
              <h2 className="text-xl font-bold text-navy mb-1">New project</h2>
              <p className="text-xs text-gray-500 mb-4">Set up a project for your own work — you can add tasks, files and people straight after.</p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Project name *</label>
                  <input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="e.g. Manchester depot relocation" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                    <input type="date" value={newForm.start_date} onChange={(e) => setNewForm({ ...newForm, start_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                    <input type="date" value={newForm.end_date} onChange={(e) => setNewForm({ ...newForm, end_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input value={newForm.location} onChange={(e) => setNewForm({ ...newForm, location: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="e.g. Manchester" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea rows="3" value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder="What is this project about?" />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowNew(false)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={busy} className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-green hover:bg-green-600 disabled:opacity-50">Create project</button>
              </div>
            </form>
          </div>
        )}
        </div>
      </Layout>
    );
  }


  // ── DETAIL ──
  return (
    <Layout onLogout={onLogout} user={user} activeTab="projects">
      <div className="w-full">
        <Link to={base} className="mb-3 shrink-0 inline-flex items-center gap-2 text-gray-500 hover:text-navy">
          <FaArrowLeft /> All projects
        </Link>

        <div className="bg-white rounded-2xl border p-5 mb-4 shrink-0" style={{ borderColor: '#E8ECEF' }}>
          <p className="text-xs font-semibold text-gray-400">{project.project_number}</p>
          <h1 className="text-2xl font-bold text-navy">{project.name}</h1>
          <p className="text-sm text-gray-500 mt-1">{fmtDate(project.start_date)} → {fmtDate(project.end_date)}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
            <div className="rounded-xl border p-3" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Progress</p>
              <p className="text-xl font-bold text-navy">{progress}%</p>
            </div>
            <div className="rounded-xl border p-3" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Tasks done</p>
              <p className="text-xl font-bold text-green-600">{done}/{tasks.length}</p>
            </div>
            <div className="rounded-xl border p-3" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">In progress</p>
              <p className="text-xl font-bold text-blue-600">{tasks.filter((t) => t.status === 'in_progress').length}</p>
            </div>
            <div className="rounded-xl border p-3" style={{ borderColor: '#E8ECEF' }}>
              <p className="text-[11px] uppercase tracking-wide text-gray-500">Your access</p>
              <p className="text-sm font-semibold text-navy">{canEdit ? 'Can add and update' : 'View only'}</p>
            </div>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-3">
            <div className="h-full bg-green" style={{ width: `${progress}%` }} />
          </div>

          {/* Share with One Purpose + who last looked (the backend logs every staff view) */}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t" style={{ borderColor: '#E8ECEF' }}>
            <div>
              <p className="text-sm font-semibold text-navy">Share with One Purpose Logistics</p>
              <p className="text-xs text-gray-500">Let our team see this project, add tasks and quote for you.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={!!project.share_with_opl}
                disabled={!canEdit}
                onChange={async (e) => {
                  const v = e.target.checked;
                  try {
                    await post(`/projects/${id}`, { share_with_opl: v }, 'PUT');
                    loadOne(id);
                    toast.success(v ? 'Shared with One Purpose' : 'No longer shared');
                  } catch (err) { toast.error(err.message); }
                }}
              />
              {project.share_with_opl ? 'Shared' : 'Private'}
            </label>
          </div>
          {Array.isArray(data?.opl_access) && data.opl_access.length > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              Last viewed by One Purpose on {fmtDate((data.opl_access[0] || {}).viewed_at || (data.opl_access[0] || {}).created_at)}
            </p>
          )}
        </div>

        {canEdit && (
          <div className="flex flex-wrap gap-2 mb-3 shrink-0">
            <button onClick={addTask} disabled={busy} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy flex items-center gap-2 disabled:opacity-50">
              <FaPlus size={11} /> Add task
            </button>
            <button onClick={addPhase} disabled={busy} className="px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50">
              <FaPlus size={11} /> Add phase
            </button>
          </div>
        )}

        <div>
          {loading ? (
            <div className="py-16 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={24} /></div>
          ) : (
            <ProjectGantt
              phases={phases}
              tasks={tasks}
              project={project}
              canEditTask={(t) => canEdit && t.created_by === user.id}
            />
          )}

        {/* Add task / add phase — must live in the DETAIL block too: the buttons that
            set addWhat are here, so mounting the modal only in the list view left it
            invisible until you navigated back. */}
        {addWhat && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setAddWhat(null)}></div>
            <form onSubmit={submitAdd} className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
              <h2 className="text-xl font-bold text-navy mb-1">{addWhat === 'phase' ? 'New phase' : 'New task'}</h2>
              <p className="text-xs text-gray-500 mb-4">
                {addWhat === 'phase'
                  ? 'Phases group your tasks — e.g. Transport, Documentation, Delivery. It appears straight away on the project schedule.'
                  : 'Give it a clear name — e.g. Book abnormal load permit. You can set dates, requirements and people on the project page.'}
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">{addWhat === 'phase' ? 'Phase name *' : 'Task name *'}</label>
              <input autoFocus value={addName} onChange={(e) => setAddName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" placeholder={addWhat === 'phase' ? 'e.g. Transport' : 'e.g. Book abnormal load permit'} />
              <div className="mt-3 space-y-3">
                {addWhat === 'task' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Which phase is it in?</label>
                    <select value={addPhaseId} onChange={(e) => setAddPhaseId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green">
                      <option value="">No phase yet (General)</option>
                      {phases.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start date</label>
                    <input type="date" value={addStart} onChange={(e) => setAddStart(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End date</label>
                    <input type="date" value={addEnd} onChange={(e) => setAddEnd(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green" />
                  </div>
                </div>
                {addWhat === 'task' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Allocate to <span className="text-gray-400 font-normal">(optional)</span></label>
                    <select value={addAssignee} onChange={(e) => setAddAssignee(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green">
                      <option value="">Nobody yet</option>
                      {(data?.members || []).map((m) => {
                        const uid = m.user_id || (m.user && m.user.id) || '';
                        const nm = m.name || (m.user && m.user.full_name) || m.member_name || m.email || 'Member';
                        const em = m.email || (m.user && m.user.email) || '';
                        return uid ? (<option key={m.id || uid} value={`${uid}|${nm}|${em}`}>{nm}</option>) : null;
                      })}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Dates left blank default to the phase, then the project, so the task always shows on the schedule.</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setAddWhat(null)} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={busy} className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-green hover:bg-green-600 disabled:opacity-50">{addWhat === 'phase' ? 'Add phase' : 'Add task'}</button>
              </div>
            </form>
          </div>
        )}
        </div>

        <div className="bg-white rounded-2xl border p-4 mt-3 shrink-0 max-h-[26vh] overflow-y-auto" style={{ borderColor: '#E8ECEF' }}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold text-navy flex items-center gap-2"><FaFileAlt className="text-green" /> Documents ({files.length})</h2>
            {canEdit && (
              <label className="px-3 py-1.5 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 cursor-pointer flex items-center gap-1">
                <FaUpload size={10} /> {busy ? 'Uploading…' : 'Upload'}
                <input type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
              </label>
            )}
          </div>
          {files.length === 0 ? (
            <p className="text-sm text-gray-400">No documents yet.</p>
          ) : (
            <div className="space-y-1">
              {files.map((f) => (
                <a key={f.id} href={f.file_url} target="_blank" rel="noreferrer" className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 text-sm hover:bg-gray-100">
                  <span className="text-navy truncate">{f.file_name || f.caption || 'Document'}</span>
                  <span className="text-xs text-gray-400 shrink-0 ml-2">{fmtDate(f.created_at)}</span>
                </a>
              ))}
            </div>
          )}
        </div>

        {canManage && (
          <PortalProjectMembers
            projectId={id}
            members={data?.members || []}
            canManage
            onChanged={() => loadOne(id)}
          />
        )}
      </div>
    </Layout>
  );
}
